import React from 'react';
import { fmtUSD, fmtPct } from '../lib/math';
import { MONTHS } from '../lib/brokerages';

export default function ResultsTable({ results, current, drillId, setDrillId }) {
  const bestAnnualNet = Math.max(...results.map((x) => x.r.annualNet));
  const bestEffSplit = Math.max(...results.map((x) => x.r.effectiveSplit));

  const metrics = [
    { label: 'Annual net take-home', key: 'annualNet', fmt: (v) => fmtUSD(v),
      best: (v) => v === bestAnnualNet, bold: true, testId: 'bta-metric-annual-net' },
    { label: 'Effective split % (net ÷ GCI)', key: 'effectiveSplit', fmt: (v) => fmtPct(v),
      best: (v) => v === bestEffSplit, testId: 'bta-metric-effective-split' },
    { label: 'Total fees paid', key: 'totalFeesPaid', fmt: (v) => fmtUSD(v),
      testId: 'bta-metric-total-fees' },
    { label: 'Cap-hit month', key: 'capHitMonth',
      fmt: (v, r) => (v == null ? (r.annualNet >= 0 ? 'Does not cap' : '—') : MONTHS[v]),
      testId: 'bta-metric-cap-month' },
    { label: 'Post-cap representative deal net', key: 'postCapNet', fmt: (v) => fmtUSD(v),
      testId: 'bta-metric-post-cap-net' },
  ];

  return (
    <div
      className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden mb-8"
      data-testid="bta-results-table"
    >
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="px-4 py-3 text-left text-slate-600 font-semibold bta-sticky-col">Metric</th>
              {results.map(({ slot }) => (
                <th
                  key={slot.id}
                  onClick={() => setDrillId(drillId === slot.id ? null : slot.id)}
                  data-testid={`bta-results-header-${slot.is_current ? 'current' : 'option'}`}
                  className={
                    'px-4 py-3 text-right font-semibold cursor-pointer hover:bg-slate-100 ' +
                    (slot.is_current ? 'bg-[#DCD9C8]/50 text-[#092825]' : 'text-slate-700')
                  }
                >
                  <div className="flex flex-col items-end">
                    <span className={'text-[10px] uppercase tracking-wider ' + (slot.is_current ? 'text-[#286C4E]' : 'text-slate-400')}>
                      {slot.is_current ? 'Current' : 'Option'}
                    </span>
                    <span>{slot.name}</span>
                    {slot.on_team && slot.team_name && (
                      <span className="text-xs text-slate-500 font-normal">+ {slot.team_name}</span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {metrics.map((m) => (
              <tr key={m.key} className="hover:bg-slate-50/50">
                <td className={'px-4 py-3 text-slate-700 bta-sticky-col ' + (m.bold ? 'font-semibold' : '')}>
                  {m.label}
                </td>
                {results.map(({ slot, r }) => {
                  const v = r[m.key];
                  const isBest = m.best && m.best(v);
                  return (
                    <td
                      key={slot.id}
                      data-testid={`${m.testId}-${slot.is_current ? 'current' : slot.brokerage_key}`}
                      className={
                        'px-4 py-3 text-right tabular-nums ' +
                        (m.bold ? 'font-bold text-lg ' : '') +
                        (isBest ? 'bg-[#2FA163]/10 text-[#286C4E]' : '')
                      }
                    >
                      {isBest && '✓ '}{m.fmt(v, r)}
                    </td>
                  );
                })}
              </tr>
            ))}
            {current && (
              <>
                <tr className="bg-slate-50">
                  <td className="px-4 py-3 text-slate-700 font-semibold bta-sticky-col">Delta vs. current ($)</td>
                  {results.map(({ slot, r }) => {
                    if (slot.is_current) {
                      return <td key={slot.id} className="px-4 py-3 text-right text-slate-400">—</td>;
                    }
                    const delta = r.annualNet - current.r.annualNet;
                    return (
                      <td
                        key={slot.id}
                        className={
                          'px-4 py-3 text-right tabular-nums font-semibold ' +
                          (delta > 0 ? 'text-[#2FA163]' : delta < 0 ? 'text-red-500' : 'text-slate-600')
                        }
                      >
                        {fmtUSD(delta, { sign: true })}
                      </td>
                    );
                  })}
                </tr>
                <tr className="bg-slate-50">
                  <td className="px-4 py-3 text-slate-700 font-semibold bta-sticky-col">Delta vs. current (%)</td>
                  {results.map(({ slot, r }) => {
                    if (slot.is_current) {
                      return <td key={slot.id} className="px-4 py-3 text-right text-slate-400">—</td>;
                    }
                    const pct = current.r.annualNet > 0 ? (r.annualNet - current.r.annualNet) / current.r.annualNet : 0;
                    return (
                      <td
                        key={slot.id}
                        className={
                          'px-4 py-3 text-right tabular-nums font-semibold ' +
                          (pct > 0 ? 'text-[#2FA163]' : pct < 0 ? 'text-red-500' : 'text-slate-600')
                        }
                      >
                        {(pct > 0 ? '+' : '')}{(pct * 100).toFixed(1)}%
                      </td>
                    );
                  })}
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
